import {
  Box,
  Button,
  FormControl,
  Grid,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import TextField from '../../../../components/SanitizedTextField/SanitizedTextField';
import PropTypes from "prop-types";
import {
  clampIsoDateToToday,
  getReportDateInputProps,
} from "../../../../utils/reportDateLimits";
import {
  renderCircleValue,
  renderDivisionValue,
  renderSubDivisionValue,
} from "../../../Report/coaHierarchySelectRenderers";
import DiscoCompanyMenuItems from "../../../../components/AdminCompanySelect/DiscoCompanyMenuItems";
import ComplaintsReportLevelFields from "./ComplaintsReportLevelFields";

const filterFieldLabelSx = {
  fontWeight: 600,
  color: "text.secondary",
  display: "block",
  minHeight: "1.35rem",
  lineHeight: 1.35,
  mb: 0.5,
};

const filterControlSx = {
  "& .MuiOutlinedInput-root": {
    height: 40,
  },
};

const FilterFieldLabel = ({ htmlFor, children }) => (
  <Typography
    component="label"
    variant="body2"
    htmlFor={htmlFor}
    className="field-label"
    sx={filterFieldLabelSx}
  >
    {children}
  </Typography>
);

FilterFieldLabel.propTypes = {
  htmlFor: PropTypes.string,
  children: PropTypes.node,
};

/** COA / Posted-for-Proof style filter row for complaint list reports. */
export default function ComplaintsHierarchyFilters({
  idPrefix = "pc",
  showGo = false,
  dateFrom,
  setDateFrom,
  dateTo,
  setDateTo,
  code,
  hierarchy,
  selectedCompanyCode,
  onChangeCompany,
  circleCode,
  onChangeCircle,
  filteredCircles,
  selectedDivision,
  onChangeDivision,
  filteredDivisions,
  selectedSubDivision,
  onChangeSubDivision,
  filteredSubDivisions,
  onGoClick,
  isGoDisabled,
  viewBackdrop,
  trailingFilters = null,
  useReportLevel = false,
  reportLevel,
  onReportLevelChange,
  availableReportLevelOptions,
  showCircleFilter,
  showDivisionFilter,
  showSubDivisionFilter,
  circleFilterEnabled,
  divisionFilterEnabled,
  subDivisionFilterEnabled,
}) {
  return (
    <Grid
      container
      spacing={2}
      alignItems="flex-end"
      className="coa-filters-grid-wrap"
    >
      <Grid item xs={12} sm={6} md={2}>
        <Stack spacing={0.5}>
          <FilterFieldLabel htmlFor={`${idPrefix}-date-from`}>
            Date From
          </FilterFieldLabel>
          <TextField
            id={`${idPrefix}-date-from`}
            fullWidth
            size="small"
            variant="outlined"
            type="date"
            value={dateFrom}
            inputProps={getReportDateInputProps()}
            onChange={(e) => setDateFrom(clampIsoDateToToday(e.target.value))}
            sx={filterControlSx}
          />
        </Stack>
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <Stack spacing={0.5}>
          <FilterFieldLabel htmlFor={`${idPrefix}-date-to`}>Date To</FilterFieldLabel>
          <TextField
            id={`${idPrefix}-date-to`}
            fullWidth
            size="small"
            variant="outlined"
            type="date"
            value={dateTo}
            inputProps={getReportDateInputProps()}
            onChange={(e) => setDateTo(clampIsoDateToToday(e.target.value))}
            sx={filterControlSx}
          />
        </Stack>
      </Grid>
      {useReportLevel ? (
        <ComplaintsReportLevelFields
          idPrefix={idPrefix}
          code={code}
          reportLevel={reportLevel}
          onReportLevelChange={onReportLevelChange}
          availableReportLevelOptions={availableReportLevelOptions}
          selectedCompanyCode={selectedCompanyCode}
          onChangeCompany={onChangeCompany}
          showCircleFilter={showCircleFilter}
          circleCode={circleCode}
          onChangeCircle={onChangeCircle}
          filteredCircles={filteredCircles}
          circleFilterEnabled={circleFilterEnabled}
          showDivisionFilter={showDivisionFilter}
          selectedDivision={selectedDivision}
          onChangeDivision={onChangeDivision}
          filteredDivisions={filteredDivisions}
          divisionFilterEnabled={divisionFilterEnabled}
          showSubDivisionFilter={showSubDivisionFilter}
          selectedSubDivision={selectedSubDivision}
          onChangeSubDivision={onChangeSubDivision}
          filteredSubDivisions={filteredSubDivisions}
          subDivisionFilterEnabled={subDivisionFilterEnabled}
        />
      ) : (
        <>
      {code == 10 && (
        <Grid item xs={12} sm={6} md={2}>
          <Stack spacing={0.5}>
            <FilterFieldLabel htmlFor={`${idPrefix}-company`}>Company</FilterFieldLabel>
            <FormControl fullWidth size="small" variant="outlined" sx={filterControlSx}>
              <Select
                id={`${idPrefix}-company`}
                displayEmpty
                value={selectedCompanyCode || ""}
                onChange={onChangeCompany}
              >
                <MenuItem value="">
                  <em>Select Company</em>
                </MenuItem>
                <DiscoCompanyMenuItems
                  currentValue={selectedCompanyCode}
                  restrictToEnabled={false}
                />
              </Select>
            </FormControl>
          </Stack>
        </Grid>
      )}
      {hierarchy <= 2 && (
        <Grid item xs={12} sm={6} md={2}>
          <Stack spacing={0.5}>
            <FilterFieldLabel htmlFor={`${idPrefix}-circle`}>Circle</FilterFieldLabel>
            <FormControl fullWidth size="small" variant="outlined" sx={filterControlSx}>
              <Select
                id={`${idPrefix}-circle`}
                displayEmpty
                value={circleCode || ""}
                onChange={onChangeCircle}
                renderValue={(v) => renderCircleValue(v, filteredCircles)}
              >
                <MenuItem value="">
                  <em>All Circle</em>
                </MenuItem>
                {filteredCircles.map((circle) => (
                  <MenuItem key={circle.circleCode} value={circle.circleCode}>
                    {circle.circleName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Grid>
      )}
      {hierarchy <= 3 && (
        <Grid item xs={12} sm={6} md={2}>
          <Stack spacing={0.5}>
            <FilterFieldLabel htmlFor={`${idPrefix}-division`}>Division</FilterFieldLabel>
            <FormControl fullWidth size="small" variant="outlined" sx={filterControlSx}>
              <Select
                id={`${idPrefix}-division`}
                displayEmpty
                value={selectedDivision || ""}
                onChange={onChangeDivision}
                disabled={!circleCode}
                renderValue={(v) => renderDivisionValue(v, filteredDivisions)}
              >
                <MenuItem value="">
                  <em>All Division</em>
                </MenuItem>
                {filteredDivisions.map((division) => (
                  <MenuItem key={division.divisionCode} value={division.divisionCode}>
                    {division.divisionName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Grid>
      )}
      {hierarchy <= 4 && (
        <Grid item xs={12} sm={6} md={2}>
          <Stack spacing={0.5}>
            <FilterFieldLabel htmlFor={`${idPrefix}-subdiv`}>
              Sub-Division
            </FilterFieldLabel>
            <FormControl fullWidth size="small" variant="outlined" sx={filterControlSx}>
              <Select
                id={`${idPrefix}-subdiv`}
                displayEmpty
                value={selectedSubDivision || ""}
                onChange={onChangeSubDivision}
                disabled={!selectedDivision}
                renderValue={(v) =>
                  renderSubDivisionValue(v, filteredSubDivisions)
                }
              >
                <MenuItem value="">
                  <em>All Sub-Division</em>
                </MenuItem>
                {filteredSubDivisions.map((subDivision) => (
                  <MenuItem
                    key={subDivision.subDivisionCode}
                    value={subDivision.subDivisionCode}
                  >
                    {subDivision.subDivisionName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Grid>
      )}
        </>
      )}
      {trailingFilters}
      {showGo && (
        <Grid item xs={12} sm={6} md={2}>
          <Stack spacing={0.5} sx={{ pt: "2px" }}>
            <Box
              aria-hidden
              sx={{
                height: "1.35rem",
                mb: 0.5,
                lineHeight: 1.35,
              }}
            />
            <Button
              id={`${idPrefix}-go-btn`}
              fullWidth
              variant="contained"
              disableElevation
              className="coa-go-btn"
              onClick={onGoClick}
              disabled={isGoDisabled || viewBackdrop}
            >
              GO
            </Button>
          </Stack>
        </Grid>
      )}
    </Grid>
  );
}

ComplaintsHierarchyFilters.propTypes = {
  idPrefix: PropTypes.string,
  showGo: PropTypes.bool,
  dateFrom: PropTypes.string,
  setDateFrom: PropTypes.func.isRequired,
  dateTo: PropTypes.string,
  setDateTo: PropTypes.func.isRequired,
  code: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  hierarchy: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectedCompanyCode: PropTypes.string,
  onChangeCompany: PropTypes.func,
  circleCode: PropTypes.string,
  onChangeCircle: PropTypes.func,
  filteredCircles: PropTypes.array,
  selectedDivision: PropTypes.string,
  onChangeDivision: PropTypes.func,
  filteredDivisions: PropTypes.array,
  selectedSubDivision: PropTypes.string,
  onChangeSubDivision: PropTypes.func,
  filteredSubDivisions: PropTypes.array,
  onGoClick: PropTypes.func,
  isGoDisabled: PropTypes.bool,
  viewBackdrop: PropTypes.bool,
  trailingFilters: PropTypes.node,
  useReportLevel: PropTypes.bool,
  reportLevel: PropTypes.string,
  onReportLevelChange: PropTypes.func,
  availableReportLevelOptions: PropTypes.array,
  showCircleFilter: PropTypes.bool,
  showDivisionFilter: PropTypes.bool,
  showSubDivisionFilter: PropTypes.bool,
  circleFilterEnabled: PropTypes.bool,
  divisionFilterEnabled: PropTypes.bool,
  subDivisionFilterEnabled: PropTypes.bool,
};
